/* ============================================================================
 * Dreame HK PDP revamp, theme runtime (Convx, 2026-09-25)
 *
 * In-theme port of the parts of the Varify variant
 * (projects/dreame-hk/html/2026-09-06_ab-pdp-atf-variant-v4.js) that need
 * JavaScript. Layout, order and styling are Liquid + assets/convx-pdp.css;
 * the sticky bar's gate is assets/sticky-cart.js. This file owns:
 *
 *   1. 產品功能 band: parses the product description into headline KSPs, a
 *      feature-tick list and a note card. Same parser and rules as v4, so
 *      all 29 live descriptions render as they did in the test.
 *   2. Anchor nav: normalises the hrefs the merchandisers store in the
 *      anchor_point setting (some are absolute URLs), drops entries whose
 *      target is not on the page, puts 產品功能 first and 跳返上頂 last,
 *      smooth-scrolls with a header offset, scroll-spies the active pill,
 *      and pins the nav directly under the fixed header on scroll.
 *   3. Rating row: overrides the score and count with the Judge.me widget's
 *      own figures when the widget is on the page, and links the count to
 *      the review section.
 *   4. Price chip inside the add-to-cart button, re-synced on variant change.
 *
 * Nothing here is hardcoded per product. Everything is read from the DOM.
 * ==========================================================================*/
(function () {
  'use strict';

  var CFG = {
    perf: {
      oneColumnAtTickLength: 30,
      largePullMaxChars: 4,
      tickMarkers: ['✅', '➕', '🏅'],
      /* Circled numbers (➊ ①) and keycap digits (1️⃣, used on the Aero
       * Straight KOL pages) both number a feature list. */
      tickMarkerPattern: /^(?:[①-⑳❶-❿➀-➓⓫-⓴]|[0-9]️?⃣)/,
      /* A dash- or pipe-led line straight after a tick is that tick's detail. */
      detailLinePattern: /^[-–—－‧•|｜]\s*/,
      /* A plain line directly above a tick block is its group heading when it
       * is short and reads as a label: no colon and no sentence punctuation.
       * 30 rather than v4's 14 so the KOL pages' "Aero Straight 3-in-1
       * 吹風護髮直髮器" subtitle is a heading, not note copy. */
      headingMaxChars: 30,
      unitNowrapMaxChars: 6,
      autoHighlight: 2,
      removeHighlightedFromList: true
    },
    nav: {
      perfLabel: '產品功能',
      backToTopLabel: '跳返上頂',
      backToTopLast: true,
      pin: false,             // round 4: the nav sits under the buy box and scrolls with the page
      reviewLabelMatch: ['用戶評價', '產品評論', '評價', '評論']
    },
    rating: {
      minReviews: 1
    },
    priceInAtcButton: true
  };

  var root = document.documentElement;
  var R = {};

  /* ---------------------------------------------------------------- utils */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function esc(t) {
    return String(t == null ? '' : t)
      .split('&').join('&amp;')
      .split('<').join('&lt;')
      .split('>').join('&gt;');
  }

  var ICON_CHECK = '<svg class="ab-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';

  function headerOffset() {
    var h = document.querySelector('sticky-header header.header, .header-wrapper header.header');
    if (!h) return 0;
    return Math.max(0, h.getBoundingClientRect().bottom);
  }

  function headerFixed() {
    var s = document.querySelector('.section-header');
    return !!(s && s.classList.contains('shopify-section-header-sticky'));
  }

  function navHeight() {
    if (!R.nav || !R.nav.classList.contains('is-pinned')) return 0;
    return R.nav.getBoundingClientRect().height;
  }

  function wantedTop(target) {
    var y = window.pageYOffset + target.getBoundingClientRect().top;
    return Math.max(0, y - (headerOffset() + navHeight() + 8));
  }

  // Long scrolls across lazy-loaded imagery move the target while the smooth
  // scroll is in flight, so wait for it to settle and correct. Measured
  // landing without this in the v2 test: ~2,900px short.
  function scrollToTarget(target) {
    if (!target) return;
    target = (window.ConvxTabs && window.ConvxTabs.reveal(target)) || target;
    window.scrollTo({ top: wantedTop(target), behavior: 'smooth' });
    var last = -1, still = 0, passes = 0, corrections = 0;
    var t = setInterval(function () {
      passes++;
      var now = Math.round(window.pageYOffset);
      if (now === last) { still++; } else { still = 0; last = now; }
      if (still >= 2) {
        var want = Math.round(wantedTop(target));
        if (Math.abs(want - now) > 24 && corrections < 3) {
          corrections++;
          still = 0;
          window.scrollTo({ top: want, behavior: 'smooth' });
        } else {
          clearInterval(t);
        }
      }
      if (passes > 60) clearInterval(t);
    }, 100);
  }

  /* Section ids embed the template's numeric id (shopify-section-template--
   * 23910291898531__faq_68XyRn), which changes every time the theme is
   * duplicated, while the merchandisers' anchor_point hrefs were written
   * against one particular copy. So an exact id match fails on every other
   * copy, including this one. The section KEY after "__" (faq_68XyRn) is
   * carried by the template JSON and survives duplication, so fall back to
   * matching on that suffix. */
  function resolveTarget(id) {
    if (!id) return null;
    var exact = document.getElementById(id);
    if (exact) return exact;
    var cut = id.lastIndexOf('__');
    if (cut === -1) return null;
    var key = id.slice(cut + 2);
    if (!key) return null;
    var safe = key.replace(/["\\]/g, '\\$&');
    return document.querySelector('.shopify-section[id$="__' + safe + '"]') ||
           document.querySelector('[id$="__' + safe + '"]');
  }

  function resolveRefs() {
    R.mainInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!R.mainInfo) return false;
    R.sid = R.mainInfo.getAttribute('data-section');
    R.section = R.mainInfo.closest('.shopify-section') || document.getElementById('shopify-section-' + R.sid);
    R.form = document.getElementById('product-form-' + R.sid);
    R.atcButton = R.form ? R.form.querySelector('button[name="add"]') : null;
    R.perf = document.querySelector('[data-cx-perf]');
    R.navSlot = document.querySelector('[data-cx-anchor-slot]');
    R.nav = R.navSlot ? R.navSlot.querySelector('[data-cx-anchor]') : null;
    return true;
  }

  /* ================================================== 1. 產品功能 band */
  function isTickLine(t) {
    for (var i = 0; i < CFG.perf.tickMarkers.length; i++) {
      if (t.indexOf(CFG.perf.tickMarkers[i]) === 0) return true;
    }
    return CFG.perf.tickMarkerPattern.test(t);
  }
  function isDetailLine(t) { return CFG.perf.detailLinePattern.test(t); }
  // The merchandiser's own numeral, so ➊➋➌ and ①②③ each keep their style. A
  // keycap (1️⃣) is an emoji, which the UI does not render; it becomes the bare
  // digit and the CSS draws the disc.
  function stepMarker(t) {
    var m = CFG.perf.tickMarkerPattern.exec(t);
    if (!m) return '';
    var k = m[0].match(/^([0-9])️?⃣$/);
    return k ? k[1] : m[0];
  }
  function stripMarkers(t) {
    t = t.replace(CFG.perf.tickMarkerPattern, '');
    for (var i = 0; i < CFG.perf.tickMarkers.length; i++) t = t.split(CFG.perf.tickMarkers[i]).join('');
    return t.trim();
  }
  function isLabelLine(t) { return /[：:]\s*$/.test(t); }

  var BLOCK_TAGS = /^(UL|OL|LI|P|DIV|H[1-6]|SECTION|ARTICLE|BLOCKQUOTE)$/;
  var INLINE_KEEP = /^(STRONG|B|EM|I|U|SUP|SUB|A)$/;

  function collectBlocks(node, out, depth) {
    if (depth > 6) { out.push(node); return; }
    var kids = node.children, hasBlockChild = false;
    for (var i = 0; i < kids.length; i++) {
      if (BLOCK_TAGS.test(kids[i].tagName)) { hasBlockChild = true; break; }
    }
    if (!hasBlockChild) { out.push(node); return; }
    for (var j = 0; j < kids.length; j++) {
      if (BLOCK_TAGS.test(kids[j].tagName)) collectBlocks(kids[j], out, depth + 1);
    }
  }

  // Split a block into one element per line at <br>, on the DOM rather than
  // on the HTML string, so a <strong> that spans a <br> becomes one balanced
  // <strong> per line instead of an unclosed tag.
  function splitOnBr(block) {
    var lines = [], open = [], line, tip;
    function newLine() {
      line = document.createElement('div');
      lines.push(line);
      tip = line;
      for (var i = 0; i < open.length; i++) {
        var w = document.createElement(open[i].tag);
        if (open[i].href) w.setAttribute('href', open[i].href);
        tip.appendChild(w);
        tip = w;
      }
    }
    newLine();
    function walk(src) {
      for (var i = 0; i < src.childNodes.length; i++) {
        var node = src.childNodes[i];
        if (node.nodeType === 3) { tip.appendChild(document.createTextNode(node.nodeValue)); continue; }
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'BR') { newLine(); continue; }
        if (!INLINE_KEEP.test(node.tagName)) { walk(node); continue; }
        var href = node.tagName === 'A' ? node.getAttribute('href') : null;
        var keep = document.createElement(node.tagName);
        if (href) keep.setAttribute('href', href);
        tip.appendChild(keep);
        open.push({ tag: node.tagName, href: href });
        tip = keep;
        walk(node);
        open.pop();
        tip = tip.parentNode;
      }
    }
    walk(block);
    return lines;
  }

  // rootEl defaults to this page's own description source. window.ConvxPdp.keyPoints (below) passes
  // another product's description, parsed off-screen, for the bundle quick view.
  function descriptionLines(rootEl) {
    if (!rootEl) rootEl = R.perf ? R.perf.querySelector('.cx-perf-source') : null;
    if (!rootEl || !(rootEl.textContent || '').trim()) return [];
    var blocks = [];
    collectBlocks(rootEl, blocks, 0);
    var lines = [];
    for (var b = 0; b < blocks.length; b++) {
      var parts = splitOnBr(blocks[b]);
      for (var j = 0; j < parts.length; j++) {
        var text = (parts[j].textContent || '').replace(/ /g, ' ').trim();
        if (text) lines.push({ html: parts[j].innerHTML, text: text });
      }
    }
    return lines;
  }

  function squash(s) { return (s || '').replace(/\s+/g, ''); }

  function readPerfContent(title, rootEl, titleText) {
    var out = { groups: [], noteHtml: '', noteHeading: '', eyebrow: title, tickCount: 0 };
    var lines = descriptionLines(rootEl);
    // Accessory descriptions are often just the product title again; that is
    // not a note, so it is neither shown in the band nor counted as content.
    var h1 = document.querySelector('.product_all .product__title h1');
    var titleKey = titleText != null ? squash(titleText) : (h1 ? squash(h1.textContent) : '');
    var group = null, lastWasTick = false;
    for (var i = 0; i < lines.length; i++) {
      var L = lines[i];
      if (titleKey && squash(L.text) === titleKey) continue;
      if (isTickLine(L.text)) {
        if (!group) { group = { heading: '', ticks: [] }; out.groups.push(group); }
        group.ticks.push({ text: stripMarkers(L.text), step: stepMarker(L.text) });
        out.tickCount++;
        lastWasTick = true;
        continue;
      }
      if (lastWasTick && group && group.ticks.length && isDetailLine(L.text)) {
        var detail = L.text.replace(CFG.perf.detailLinePattern, '').trim();
        if (detail) group.ticks[group.ticks.length - 1].text += ' | ' + detail;
        continue;
      }
      lastWasTick = false;
      var next = lines[i + 1];
      var looksLikeHeading = L.text.length <= CFG.perf.headingMaxChars && !/[：:。！!？?]/.test(L.text);
      if (next && isTickLine(next.text) && looksLikeHeading) {
        group = { heading: (L.text === out.eyebrow) ? '' : L.text, ticks: [] };
        out.groups.push(group);
      } else if (isLabelLine(L.text) && !out.noteHeading && !out.noteHtml) {
        out.noteHeading = L.text.replace(/[：:]\s*$/, '').trim();
      } else {
        out.noteHtml += '<p>' + L.html + '</p>';
      }
    }
    return out;
  }

  function tickToKsp(tick) {
    var parts = String(tick).split(/\s*[|｜]\s*/);
    var head = (parts[0] || '').trim();
    var caption = parts.length > 1 ? parts.slice(1).join(' | ').trim() : '';
    var m = head.match(/^([0-9][0-9.,]*(?:\s?[A-Za-z%°µ³\/]+)?|[A-Za-z][A-Za-z0-9.\-™+]*)\s+([\s\S]+)$/);
    if (m && m[2] && m[1].trim().length >= 3) return { value: m[1].trim(), unit: m[2].trim(), caption: caption };
    return { value: head, unit: '', caption: caption };
  }

  function buildPerfBand() {
    if (!R.perf) return;
    var grid = R.perf.querySelector('[data-cx-perf-grid]');
    if (!grid) return;
    var title = R.perf.getAttribute('data-cx-perf-title') || CFG.nav.perfLabel;

    var live = readPerfContent(title);
    // The note card (offer copy, caveats) is retired once the product carries
    // either custom.pdp_buy_note (offers, under the ATC) or
    // custom.pdp_usage_note (使用小提醒, under this band). Until then it still
    // renders here so nothing disappears before the copy has been migrated.
    var hasBuyNote = !!document.querySelector('[data-cx-buynote], [data-cx-usagenote]');
    var showNote = !hasBuyNote && !!(live.noteHtml || live.noteHeading);
    if (!live.tickCount && !showNote) {
      R.perf.setAttribute('hidden', '');
      R.perf = null;
      return;
    }

    var pulls = [];
    if (CFG.perf.autoHighlight > 0 && live.groups.length) {
      var pool = live.groups[0].ticks;
      var eligible = 0;
      while (eligible < pool.length && !pool[eligible].step) eligible++;
      var take = Math.min(CFG.perf.autoHighlight, eligible);
      for (var ai = 0; ai < take; ai++) pulls.push(tickToKsp(pool[ai].text));
      if (CFG.perf.removeHighlightedFromList) {
        live.groups[0].ticks = pool.slice(take);
        live.tickCount -= take;
      }
    }

    var hasSteps = false, hasHeadings = false;
    for (var g = 0; g < live.groups.length; g++) {
      if (live.groups[g].heading) hasHeadings = true;
      for (var n = 0; n < live.groups[g].ticks.length; n++) {
        if (live.groups[g].ticks[n].step) hasSteps = true;
      }
    }

    function tickListHtml(ticks) {
      var lis = '';
      for (var i = 0; i < ticks.length; i++) {
        var tk = ticks[i];
        var mark = tk.step
          ? '<span class="ab-step' + (/^[0-9]$/.test(tk.step) ? ' ab-step--num' : '') + '" aria-hidden="true">' + esc(tk.step) + '</span>'
          : ICON_CHECK;
        lis += '<li>' + mark + '<span>' + esc(tk.text) + '</span></li>';
      }
      return lis ? '<ul class="ab-ticks ab-ticks--one">' + lis + '</ul>' : '';
    }

    var ticksHtml = '';
    if (hasHeadings) {
      for (var gi = 0; gi < live.groups.length; gi++) {
        var grp = live.groups[gi];
        if (!grp.ticks.length) continue;
        ticksHtml += '<div class="ab-tickgroup">' +
          (grp.heading ? '<div class="ab-tickgroup-h">' + esc(grp.heading) + '</div>' : '') +
          tickListHtml(grp.ticks) + '</div>';
      }
      if (ticksHtml) ticksHtml = '<div class="ab-tickgroups">' + ticksHtml + '</div>';
    } else {
      var flat = [];
      for (var fg = 0; fg < live.groups.length; fg++) flat = flat.concat(live.groups[fg].ticks);
      ticksHtml = tickListHtml(flat);
    }

    var compact = pulls.length > 1;
    for (var q = 0; q < pulls.length; q++) {
      if (String(pulls[q].value).length > CFG.perf.largePullMaxChars) compact = true;
    }
    var pullsHtml = '';
    for (var r = 0; r < pulls.length; r++) {
      var pl = pulls[r];
      var unitCls = 'ab-unit' + (String(pl.unit || '').length <= CFG.perf.unitNowrapMaxChars ? ' ab-unit--nowrap' : '');
      pullsHtml += '<div class="ab-pull' + (compact ? ' ab-pull--sm' : '') + '">' + esc(pl.value) +
        (pl.unit ? '<wbr><span class="' + unitCls + '">' + esc(pl.unit) + '</span>' : '') +
        (pl.caption ? '<small>' + esc(pl.caption) + '</small>' : '') + '</div>';
    }
    var pullsClass = 'ab-pulls ab-pulls--' + Math.min(pulls.length, 3);

    var head = '<div class="ab-eyebrow">' + esc(title) + '</div>';
    if (pulls.length) head += '<div class="' + pullsClass + '">' + pullsHtml + '</div>';
    head += ticksHtml;
    grid.innerHTML = head;

    var wrap = grid.parentNode;
    var oldNote = wrap.querySelector('.ab-note');
    if (oldNote) oldNote.parentNode.removeChild(oldNote);
    if (showNote) {
      wrap.appendChild(el('div', 'ab-note',
        (live.noteHeading ? '<div class="ab-note-h">' + esc(live.noteHeading) + '</div>' : '') + live.noteHtml));
    }
    R.perf.classList.toggle('ab-perf--steps', hasSteps);
    R.perf.classList.add('is-built');
  }

  /* Exposed for assets/convx-buy-options.js (2026-10-22): the first N 產品功能 points of any product's
   * description HTML, by the same parser and rules the band above uses. The HTML is parsed with
   * DOMParser, so nothing in it loads or runs. Returns [] for an empty description or one that
   * only repeats the product title. */
  function keyPoints(html, limit, productTitle) {
    if (!html || !String(html).trim()) return [];
    var doc = new DOMParser().parseFromString('<div id="cx-kp">' + html + '</div>', 'text/html');
    var rootEl = doc.getElementById('cx-kp');
    if (!rootEl) return [];
    var content = readPerfContent('', rootEl, productTitle || '');
    var out = [];
    for (var g = 0; g < content.groups.length; g++) {
      for (var t = 0; t < content.groups[g].ticks.length; t++) {
        var text = String(content.groups[g].ticks[t].text).split(/\s*[|｜]\s*/)[0].trim();
        if (text) out.push(text);
      }
    }
    return limit > 0 ? out.slice(0, limit) : out;
  }

  /* ==================================================== 2. anchor nav */
  function navItems() {
    var items = [];
    if (!R.nav) return items;
    var ps = R.nav.querySelectorAll('.anchor_point_inner p');
    for (var i = 0; i < ps.length; i++) {
      var a = ps[i].querySelector('a[href]');
      if (!a) { ps[i].parentNode.removeChild(ps[i]); continue; }
      var href = a.getAttribute('href') || '';
      var hash = href.indexOf('#') !== -1 ? href.slice(href.indexOf('#') + 1) : '';
      var label = (a.textContent || '').trim();
      if (!label) { ps[i].parentNode.removeChild(ps[i]); continue; }
      items.push({ p: ps[i], a: a, label: label, id: hash });
    }
    return items;
  }

  function findReviewSection(items) {
    for (var i = 0; i < items.length; i++) {
      for (var j = 0; j < CFG.nav.reviewLabelMatch.length; j++) {
        if (items[i].label.indexOf(CFG.nav.reviewLabelMatch[j]) === -1) continue;
        var node = resolveTarget(items[i].id);
        if (node) return node;
      }
    }
    var w = document.querySelector('.jdgm-rev-widg, .jdgm-widget, [class*="jdgm-review-widget"]');
    return w ? w.closest('[id^="shopify-section-"]') : null;
  }

  function buildAnchorNav() {
    if (!R.nav) return [];
    var inner = R.nav.querySelector('.anchor_point_inner');
    var items = navItems();
    if (!items.length) { R.navSlot.setAttribute('hidden', ''); R.nav = null; return []; }

    // 產品功能 first, pointing at the band, only when the band exists.
    if (R.perf) {
      var has = false;
      for (var k = 0; k < items.length; k++) if (items[k].label.indexOf(CFG.nav.perfLabel) !== -1) has = true;
      if (!has) {
        var p = el('p'), a = el('a');
        a.href = '#' + R.perf.id;
        a.textContent = CFG.nav.perfLabel;
        p.appendChild(a);
        inner.insertBefore(p, inner.firstChild);
        items.unshift({ p: p, a: a, label: CFG.nav.perfLabel, id: R.perf.id });
      }
    }

    var live = [];
    var topItems = [];
    for (var n = 0; n < items.length; n++) {
      var it = items[n];
      var isTop = it.label.indexOf(CFG.nav.backToTopLabel) !== -1;
      var target = isTop ? null : resolveTarget(it.id);
      if (!isTop && !target) {
        it.p.parentNode.removeChild(it.p);   // dead link: target not on this page
        continue;
      }
      it.a.setAttribute('href', isTop ? '#' : '#' + (target.id || it.id));
      if (isTop) {
        topItems.push(it);
        it.a.addEventListener('click', function (e) {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      } else {
        live.push({ link: it.a, p: it.p, target: target });
        (function (t) {
          it.a.addEventListener('click', function (e) {
            e.preventDefault();
            scrollToTarget(t);
          });
        })(target);
      }
    }
    // Follow the page, not the order the merchandisers typed into each
    // template: the PDP tabs build moves sections (FAQ now sits above reviews).
    live.sort(function (a, b) {
      if (a.target === b.target) return 0;
      return (a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
    });
    for (var s = 0; s < live.length; s++) inner.appendChild(live[s].p);
    if (CFG.nav.backToTopLast) {
      for (var b = 0; b < topItems.length; b++) inner.appendChild(topItems[b].p);
    }
    if (!inner.querySelector('p')) { R.navSlot.setAttribute('hidden', ''); R.nav = null; return []; }

    // A static nav scrolls away, so a scroll-spy highlight would only mark
    // some section the shopper is not looking at; spy only when pinned.
    if (CFG.nav.pin) {
      setupScrollSpy(live, inner);
      setupNavPin();
    }
    return items;
  }

  function setupScrollSpy(live, inner) {
    if (!live.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      var best = null;
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        if (!best || entries[i].intersectionRatio > best.intersectionRatio) best = entries[i];
      }
      if (!best) return;
      for (var j = 0; j < live.length; j++) {
        var on = live[j].target === best.target;
        live[j].p.classList.toggle('active', on);
        if (on && inner.scrollWidth > inner.clientWidth) {
          var l = live[j].link;
          var want = l.offsetLeft - (inner.clientWidth - l.offsetWidth) / 2;
          try { inner.scrollTo({ left: Math.max(0, want), behavior: 'smooth' }); }
          catch (err) { inner.scrollLeft = Math.max(0, want); }
        }
      }
    }, { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.05, 0.25, 0.5] });
    for (var m = 0; m < live.length; m++) io.observe(live[m].target);
  }

  // Pins the nav directly under the header once the header itself is fixed.
  // The slot keeps the nav's height in flow so nothing below it jumps, and
  // --cx-nav-h lets the desktop gallery's sticky offset clear the nav.
  function setupNavPin() {
    if (!R.nav || !R.navSlot) return;
    var pinned = false, queued = false;
    function apply() {
      queued = false;
      var hb = headerOffset();
      var navH = Math.round(R.nav.getBoundingClientRect().height);
      root.style.setProperty('--cx-nav-h', navH + 'px');
      var shouldPin = headerFixed() && R.navSlot.getBoundingClientRect().top <= hb + 1;
      if (shouldPin !== pinned) {
        pinned = shouldPin;
        R.nav.classList.toggle('is-pinned', pinned);
        R.navSlot.style.height = pinned ? navH + 'px' : '';
      }
      R.nav.style.top = pinned ? hb + 'px' : '';
    }
    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    }
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('orientationchange', onScroll);
    window.addEventListener('load', apply);
  }

  /* ================================================== 3. rating row */
  function readReviewStats() {
    var w = document.querySelector('.jdgm-rev-widg[data-average-rating]') ||
            document.querySelector('.jdgm-prev-badge[data-average-rating]') ||
            document.querySelector('[data-average-rating][data-number-of-reviews]');
    var avg = null, count = null;
    if (w) {
      var a = parseFloat(w.getAttribute('data-average-rating'));
      var c = parseInt(w.getAttribute('data-number-of-reviews'), 10);
      if (!isNaN(a) && a > 0) avg = Math.round(a * 10) / 10;
      if (!isNaN(c)) count = c;
    }
    return { avg: avg, count: count };
  }

  function decorateRating(items) {
    var wraps = document.querySelectorAll('.product_all .rating-wrapper');
    if (!wraps.length) return;
    var stats = readReviewStats();
    var target = findReviewSection(items || []);

    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      var score = wrap.querySelector('[data-cx-rating-score]');
      var link = wrap.querySelector('[data-cx-review-link]');
      var countEl = wrap.querySelector('[data-cx-rating-count]');

      // Judge.me widget on the page: its figures win, so the ATF and the
      // review section always agree.
      if (stats.avg != null) {
        if (!score) {
          score = el('span', 'cx-rating-score');
          score.setAttribute('data-cx-rating-score', '');
          // score sits between the stars and the count link
          if (link) wrap.insertBefore(score, link); else wrap.appendChild(score);
        }
        score.textContent = String(stats.avg);
        var star = wrap.querySelector('.rating-star');
        if (star) star.style.setProperty('--rating', String(stats.avg));
      }
      if (stats.count != null) {
        if (stats.count >= CFG.rating.minReviews) {
          if (!link) {
            link = el('a', 'cx-rating-link', '<span data-cx-rating-count></span> 條評論');
            link.setAttribute('data-cx-review-link', '');
            link.href = '#cx-reviews';
            wrap.appendChild(link);
            countEl = link.querySelector('[data-cx-rating-count]');
          }
          countEl.textContent = String(stats.count);
        } else if (link) {
          link.parentNode.removeChild(link);
          link = null;
        }
      }

      if (!link) continue;
      if (target) {
        link.setAttribute('href', '#' + target.id);
        (function (t) {
          link.addEventListener('click', function (e) {
            e.preventDefault();
            scrollToTarget(t);
          });
        })(target);
      } else {
        // No review section on this page: keep the figure, drop the dead link.
        var span = el('span', 'cx-rating-link cx-rating-link--static', link.innerHTML);
        link.parentNode.replaceChild(span, link);
      }
    }
  }

  /* ========================================= 4. price in the ATC button */
  // Tag-qualified to span: the volume-pricing block wraps the sale price in a
  // <dl class="price-per-item--current"> that precedes the span, and the
  // compare-at price is an <s>, so an unqualified selector picks the wrong node.
  function currentPriceText() {
    var box = document.getElementById('price-' + R.sid);
    if (!box) return '';
    var node = box.querySelector('span.price-item--sale, span.price-per-item--current') ||
               box.querySelector('span.price-item--regular');
    if (!node) return '';
    var raw = (node.textContent || '').replace(/\s+/g, ' ').replace(/\/\s*每項/g, '').trim();
    var tokens = raw.match(/(?:HK)?\$\s?[\d,]+(?:\.\d+)?/g);
    if (tokens && tokens.length) return tokens[tokens.length - 1].replace(/\s/g, '');
    return raw;
  }

  // Appended as a direct child of the button, not inside its <span>: the theme
  // overwrites that span's innerHTML on variant change.
  function syncAtcPrice() {
    if (!CFG.priceInAtcButton || !R.atcButton) return;
    var chip = R.atcButton.querySelector('.cx-atc-price');
    if (R.atcButton.disabled) { if (chip) chip.textContent = ''; return; }
    var p = currentPriceText();
    if (!p) return;
    if (!chip) {
      chip = el('span', 'cx-atc-price');
      R.atcButton.appendChild(chip);
    }
    chip.textContent = p;
  }

  window.ConvxPdp = window.ConvxPdp || {};
  window.ConvxPdp.keyPoints = keyPoints;
  window.ConvxPdp.syncAtcPrice = function () {
    if (!R.mainInfo) return;
    R.atcButton = R.form ? R.form.querySelector('button[name="add"]') : R.atcButton;
    syncAtcPrice();
  };

  function onVariantChanged(e) {
    if (e && e.detail && e.detail.sectionId && e.detail.sectionId !== R.sid) return;
    R.atcButton = R.form ? R.form.querySelector('button[name="add"]') : R.atcButton;
    syncAtcPrice();
  }

  /* ============================================================== boot */
  function init() {
    if (root.dataset.cxPdpDone) return;
    if (!resolveRefs()) return;
    root.dataset.cxPdpDone = '1';

    buildPerfBand();
    var items = buildAnchorNav();
    decorateRating(items);
    syncAtcPrice();
    document.addEventListener('afterVariantChanged', onVariantChanged);
    root.classList.add('cx-pdp-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
